import { Quote, Star, ArrowUpRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { useLanguage } from "../lib/LanguageContext";

const REVIEWS_BY_LANG = {
  en: [
    {
      quote:
        "The apartments were in a lovely garden setting with private pool and outdoor seating and dining facilities. The apartment was cosy and had everything we needed. The host and her mother were very welcoming and friendly.",
      name: "Beth",
      country: "United Kingdom",
    },
    {
      quote:
        "Best place for a peaceful vacation with pool activities, great garden and amazing view. Accommodation is clean and tidy and Voulisma beach is 4 min ride away. We even got some olive oil and raki as a welcome gift.",
      name: "Lazar",
      country: "Serbia",
    },
    {
      quote:
        "We loved the location out in the country but just a few minutes from town. The grounds are amazing with trees and plants everywhere and a beautiful pool area. The owner was very friendly and helpful.",
      name: "John",
      country: "United States",
    },
    {
      quote:
        "Hidden apartments in her garden with dual aspect for shade or sun. Such excellent value — thank you.",
      name: "Sarah",
      country: "United Kingdom",
    },
  ],
  el: [
    {
      quote:
        "Τα διαμερίσματα βρίσκονται σε έναν υπέροχο κήπο με ιδιωτική πισίνα και χώρους εξωτερικού καθιστικού και γεύματος. Άνετο διαμέρισμα με όλες τις ανέσεις. Η οικοδέσποινα και η μητέρα της ήταν εξαιρετικά φιλόξενες.",
      name: "Beth",
      country: "Ηνωμένο Βασίλειο",
    },
    {
      quote:
        "Το καλύτερο μέρος για ήρεμες διακοπές με πισίνα, υπέροχο κήπο και εκπληκτική θέα. Καθαρό και τακτοποιημένο, η παραλία Βούλισμα είναι 4 λεπτά μακριά. Μας έκαναν δώρο ακόμα και ελαιόλαδο και ρακή.",
      name: "Lazar",
      country: "Σερβία",
    },
    {
      quote:
        "Λατρέψαμε την τοποθεσία στην εξοχή, μόλις λίγα λεπτά από την πόλη. Καταπληκτικός κήπος με δέντρα και υπέροχη πισίνα. Ο ιδιοκτήτης πολύ φιλικός και εξυπηρετικός.",
      name: "John",
      country: "Η.Π.Α.",
    },
    {
      quote:
        "Κρυμμένα διαμερίσματα στον κήπο της, με διπλή έκθεση για σκιά ή ήλιο. Εξαιρετική σχέση ποιότητας-τιμής — σας ευχαριστούμε.",
      name: "Sarah",
      country: "Ηνωμένο Βασίλειο",
    },
  ],
};

export const ReviewBlock = () => {
  const ref = useReveal();
  const { t, lang } = useLanguage();
  const reviews = REVIEWS_BY_LANG[lang] || REVIEWS_BY_LANG.en;

  const categories = [
    { label: t("rev_cat_clean"), value: "9.4" },
    { label: t("rev_cat_value"), value: "9.5" },
    { label: t("rev_cat_host"), value: "9.8" },
    { label: t("rev_cat_loc"), value: "9.2" },
  ];

  return (
    <section
      data-testid="review-section"
      className="py-24 md:py-32 bg-[#faf9f6]"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#6b705c] mb-6">
              {t("rev_eyebrow")}
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-7xl md:text-8xl font-light text-[#1a5b73] leading-none">
                9.2
              </span>
              <span className="text-[#0a192f] font-serif text-xl">/ 10</span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className="h-3.5 w-3.5 fill-[#d4a73c] text-[#d4a73c]"
                    strokeWidth={1}
                  />
                ))}
              </div>
              <span className="text-[#0a192f] font-medium">
                {t("rev_wonderful")}
              </span>
            </div>
            <p className="mt-3 text-[#4a4a4a] text-sm">
              {t("rev_from")}{" "}
              <span className="font-medium text-[#0a192f]">247</span>{" "}
              {t("rev_verified")}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-[#0a192f]/10 pt-8">
              {categories.map((c) => (
                <div key={c.label}>
                  <div className="font-serif text-3xl text-[#0a192f]">
                    {c.value}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c] mt-1">
                    {c.label}
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://www.booking.com/hotel/gr/athina-villas-istron.html#blockdisplay4"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-all-reviews"
              className="group mt-10 inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-[#1a5b73] hover:text-[#0a192f] transition-colors"
            >
              View all reviews on Booking.com
              <ArrowUpRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={1.5}
              />
            </a>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {reviews.map((r, i) => (
              <div
                key={i}
                data-testid={`review-card-${i}`}
                className="relative bg-white border border-[#0a192f]/10 rounded-3xl p-7 md:p-8 flex flex-col"
              >
                <Quote
                  className="absolute -top-2 left-5 h-10 w-10 text-[#1a5b73]/15"
                  strokeWidth={1}
                />
                <p className="relative font-serif text-lg md:text-xl font-light text-[#0a192f] leading-snug">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="mt-6 pt-5 border-t border-[#0a192f]/10 flex items-center justify-between">
                  <span className="text-[#0a192f] font-medium text-sm">
                    {r.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c]">
                    {r.country}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
